import { useMemo } from 'react';
import { useParams } from 'react-router';
import { useSelector } from 'react-redux';
import { useQuery } from 'react-fetching-library';
import { fetchAccountAction } from '../api';

/**
 * Resolve the display name of the account the current page is scoped to.
 *
 * DS users reach the upload screens for a CHILD account via
 * /:currentAccountUuid/accounts/:accountUuid/... — the child's name is not
 * in metadata, so we fetch it by uuid (the same GET AccountEdit uses).
 * When there is no :accountUuid param the page is scoped to the user's own
 * current account, whose name is already in metadata, so no request runs.
 *
 * The name used to be smuggled through the URL as a :title path segment,
 * which broke for accounts whose name contains URL-significant characters
 * (e.g. "test #2" — the # truncated the path and the route failed to
 * match). Sourcing the name here keeps it out of the URL entirely.
 *
 * Returns `{ name, loading }`; `loading` lets the page show a title
 * skeleton while the child-account fetch is in flight. It is always false
 * for the current account, whose name resolves synchronously.
 */
export const useAccountName = () => {
  const { accountUuid } = useParams();
  const currentAccountName = useSelector(
    state => state.user?.metadata?.currentAccount?.businessName
  );

  const action = useMemo(() => fetchAccountAction({ uuid: accountUuid }), [accountUuid]);
  // initFetch only when there is a child account uuid to resolve.
  const { payload, loading, error } = useQuery(action, Boolean(accountUuid));

  if (!accountUuid) return { name: currentAccountName || '', loading: false };

  const name = payload?.businessName || '';
  // Keep loading until the name arrives or the request fails, so there is
  // no empty-title flicker between mount and the first response.
  return { name, loading: loading || (!name && !error) };
};

export default useAccountName;
